<script setup lang="ts">
import { useElementSize, useDebounceFn } from '@vueuse/core'
import { customAlphabet } from 'nanoid'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { ProductDetail, File } from '~/types/api'
import type { Configuration } from '~/types/configurator'

const props = defineProps<{
  product: ProductDetail
  productNid: number
  productSetNid: number
  configurationNid?: number
  forceFetchKey?: number
  imagePlaceholder?: File
  configuratorIsVisible?: boolean
  filesIsVisible?: boolean
  productDetailsIsVisible?: boolean
}>()

const { t } = useI18n()
const configurationFetcher = useConfigurationFetcher()

const toast = useToast()
function showConfigurationErrorToast(description: string) {
  if (import.meta.client) {
    toast.add({
      title: t('configurator.errors.toastTitle'),
      description,
      color: 'red',
    })
  }
}

const loading = ref(true)

const configuration: Ref<Configuration | null> = ref(null)
const count = ref(1)
const sharedConfigurationSlug = ref<string | null>(null)

const route = useRoute()
const { setQuery } = useQuery()
if (route.query.config && typeof route.query.config === 'string') {
  const { data, error } = await useAsyncData('sharedConfigurationBySlug', () =>
    configurationFetcher.getUserConfigurationBySlug(
      route.query.config as string
    )
  )

  if (error.value) {
    showConfigurationErrorToast(t('configurator.configurationNotFound'))
  } else {
    configuration.value = data.value?.configuration || null
    count.value = data.value?.count || 1
  }

  sharedConfigurationSlug.value = route.query.config as string

  if (import.meta.client) {
    setQuery({ config: undefined })
  }
}

const { data: fetchedConfiguration, execute: fetchConfiguration } =
  useAsyncData(
    'preConfigured',
    () =>
      configurationFetcher.getPreConfigured(
        props.productNid,
        props.configurationNid
      ),
    {
      watch: [
        () => props.productNid,
        () => props.configurationNid,
        () => props.forceFetchKey,
      ],
      immediate: false,
    }
  )

if (!configuration.value) {
  await fetchConfiguration()
  configuration.value = fetchedConfiguration.value
}

watch(fetchedConfiguration, async (newConfiguration) => {
  configuration.value = newConfiguration
})

const fatalError: Ref<string | null> = ref(null)
if (!configuration.value) {
  fatalError.value = t('configurator.errors.preConfiguredNotFound')
  showConfigurationErrorToast(t('configurator.errors.preConfiguredNotFound'))
}

const userStore = useUser()
const { isLoggedIn, userCustomerGroupId } = storeToRefs(userStore)
const userConfigStore = useUserConfig()
const { customerGroupId, priceCategoryId } = storeToRefs(userConfigStore)

watch(
  [isLoggedIn, userCustomerGroupId, customerGroupId, priceCategoryId],
  () => {
    loadOptions()
  }
)

const {
  values,
  multiselectableAccessories,
  currentValues,
  globalSurfaceChanges,
  activeSurfaceOption,
  chosenParts,
  surfaceCategoryMismatch,
  surfaceOptionsGroups,
  surfaceTypes,
  surfaceMaps,
  optionalBaseParts,
  setChosenParts,
  updateChosenPartsSurfaces,
  setGlobalSurfaceChange,
  updateNonRemovableAccessories,
  toggleOptionalBasePart,
  checkAccessoryChange,
  applyAccessoryChanges,
} = useConfiguration(configuration)

const { data: configuration3d, error: error3d } = await useAsyncData(
  'configuration3d',
  () =>
    configurationFetcher.getConfiguration3d(
      configuration.value?.productNid || props.productNid,
      currentValues.value
    )
)

watch(
  () => currentValues.value,
  async () => {
    if (configuration.value) {
      await loadConfiguration()
    }
  },
  { immediate: true }
)

if (error3d.value && !fatalError.value) {
  console.error(error3d.value)
  showConfigurationErrorToast(t('configurator.errors.couldNotLoad3DModel'))
}

const configuration3dValid = computed(() => {
  return !!(
    configuration3d.value?.nonRemovableAccessoriesValid &&
    configuration3d.value?.basePartsValid
  )
})

function setGlobalSurfaceChanges(
  surfaceOptionsGroupsKey: string,
  surfaceOptionGroupsKey: number,
  globalSurfaceChange: boolean,
  value?: Configuration['values'][number]
) {
  setGlobalSurfaceChange(
    surfaceOptionsGroupsKey,
    surfaceOptionGroupsKey,
    globalSurfaceChange,
    value
  )
  if (globalSurfaceChange && value) {
    loadConfiguration()
  }
}

function updateSurfaces(
  concreteOption: Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces'][number]
) {
  updateChosenPartsSurfaces(
    concreteOption.nid,
    concreteOption.translationOfNid,
    concreteOption.image?.url,
    concreteOption.title
  )
  updateNonRemovableAccessories()
  loadConfiguration()
}

async function loadOptions(newValue?: {
  partCategoryTranslationOfNid: number
  metaAttributeTranslationOfNid: number
}) {
  if (!configuration.value) return

  try {
    configuration.value = await configurationFetcher.getOptions(
      configuration.value.productNid,
      currentValues.value,
      newValue
    )
  } catch {
    showConfigurationErrorToast(t('configurator.errors.couldNotApplyChanges'))
  }
}

async function loadConfiguration(
  partCategoryTranslationOfNid?: number,
  metaAttributeTranslationOfNid?: number
) {
  if (loading.value) return
  loading.value = true

  if (partCategoryTranslationOfNid && metaAttributeTranslationOfNid) {
    await loadOptions({
      partCategoryTranslationOfNid,
      metaAttributeTranslationOfNid,
    })
  }

  if (configuration.value) {
    try {
      configuration3d.value = await configurationFetcher.getConfiguration3d(
        configuration.value.productNid,
        currentValues.value
      )
    } catch (error) {
      console.error(error)
      showConfigurationErrorToast(t('configurator.errors.couldNotLoad3DModel'))
    }
  }

  if (!loading.value) {
    return
  }

  if (configuration3d.value && configuration3dValid.value) {
    try {
      await world.loadConfiguration(configuration3d.value)
    } catch (error) {
      console.error(error)
      showConfigurationErrorToast(t('configurator.errors.couldNotLoad3DModel'))
    }
    // loadAR()
  }

  loading.value = false
}

async function resetConfiguration() {
  if (loading.value) return
  loading.value = true

  try {
    configuration.value = await configurationFetcher.getPreConfigured(
      props.productNid
    )
  } catch {
    showConfigurationErrorToast(t('configurator.errors.preConfiguredNotFound'))
  }

  if (configuration.value) {
    try {
      configuration3d.value = await configurationFetcher.getConfiguration3d(
        props.productNid,
        configuration.value.values
      )
    } catch (error) {
      console.error(error)
      showConfigurationErrorToast(t('configurator.errors.couldNotLoad3DModel'))
    }
  }

  if (!loading.value) {
    return
  }

  if (configuration3d.value && configuration3dValid.value) {
    try {
      await world.loadConfiguration(configuration3d.value)
    } catch (error) {
      console.error(error)
      showConfigurationErrorToast(t('configurator.errors.couldNotLoad3DModel'))
    }
  }

  loading.value = false
}

const changeModal = ref(false)
const changeOrigin = ref<{
  accessoriesOption: Configuration['accessoriesOptions'][number]
  accessory: Configuration['accessoriesOptions'][number]['values'][number]
} | null>(null)
const changedAccessories = ref<{
  addedAccessories: {
    accessoriesOption: Configuration['accessoriesOptions'][number]
    accessory: Configuration['accessoriesOptions'][number]['values'][number]
  }[]
  removedAccessories: {
    accessoriesOption: Configuration['accessoriesOptions'][number]
    accessory: Configuration['accessoriesOptions'][number]['values'][number]
  }[]
  swappedAccessories: {
    accessoriesOption: Configuration['accessoriesOptions'][number]
    oldAccessory: Configuration['accessoriesOptions'][number]['values'][number]
    newAccessory: Configuration['accessoriesOptions'][number]['values'][number]
  }[]
}>({
  addedAccessories: [],
  removedAccessories: [],
  swappedAccessories: [],
})

async function checkAndApplyAccessoryChange(
  accessoriesOption: Configuration['accessoriesOptions'][number],
  accessory: Configuration['accessoriesOptions'][number]['values'][number]
) {
  const changes = await checkAccessoryChange(accessoriesOption, accessory)
  if (!changes) {
    showConfigurationErrorToast(t('configurator.errors.couldNotApplyChanges'))
  } else {
    const { addedAccessories, removedAccessories, swappedAccessories } =
      applyAccessoryChanges(accessory, changes)
    if (
      addedAccessories.length > 0 ||
      removedAccessories.length > 0 ||
      swappedAccessories.length > 0
    ) {
      changedAccessories.value = {
        addedAccessories,
        removedAccessories,
        swappedAccessories,
      }
      changeOrigin.value = {
        accessoriesOption,
        accessory,
      }
      changeModal.value = true
    }

    loadConfiguration()
  }
}

const requestOpen = ref(false)
const requestSubmitTrigger = ref(0)
const requestValid = ref(false)

const shareModalOpen = ref(false)

const {
  configurationLink,
  configurationSlug,
  configurationLinkLoading,
  configurationLinkDirty,
  getConfigurationLink,
} = useConfigurationLink(
  configuration,
  currentValues,
  count,
  sharedConfigurationSlug
)

watch(
  () => [shareModalOpen.value, requestOpen.value],
  () => {
    if (
      (shareModalOpen.value || requestOpen.value) &&
      configurationLinkDirty.value
    ) {
      getConfigurationLink()
    }
  }
)

async function openPdfPage() {
  await getConfigurationLink()

  window.open(
    localePath({
      name: 'pdf-configuration-id',
      params: {
        id: configurationSlug.value,
      },
    }),
    '_blank'
  )
}

const isDifferentProductSet = computed(() => {
  return configuration.value?.productSetNid !== props.product.productSetNid
})
const isClosed = ref(false)

const localePath = useLocalePath()

async function navigateToConfigurationLink() {
  await getConfigurationLink()
  navigateTo(
    localePath({
      name: 'line-slug',
      params: {
        line: configuration.value?.productLineSlug,
        slug: configuration.value?.productSetSlug,
      },
      query: { config: configurationSlug.value },
    })
  )
}

const { surfaceOptionsGroupsPrices, price } = useConfigurationPrice(
  configuration,
  currentValues,
  surfaceTypes,
  surfaceMaps,
  surfaceOptionsGroups,
  surfaceCategoryMismatch
)

const { activePanel, panels } = useConfiguratorPanels(
  configuration,
  surfaceOptionsGroups
)

const configurator3d = useConfigurator3d()
const world = useWorld()
const { stopReset, reset } = useCamera()
const { download3dModel } = useDownload()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const configuratorContainerRef = ref<HTMLDivElement | null>(null)
const { width: canvasWidth, height: canvasHeight } = useElementSize(canvasRef)

const mounted = ref(false)

const { arMode, selectARMode, activateAR } = useAR()

const clickCount = ref(0)

const { sendStartConfigurator, sendConfigurator10PlusClicks } = useEvent()

function handleConfiguratorClick() {
  clickCount.value++
  if (clickCount.value === 1) {
    sendStartConfigurator(
      configuration.value?.productLineTitle +
        ' ' +
        configuration.value?.productTitle
    )
  } else if (clickCount.value === 10) {
    sendConfigurator10PlusClicks(
      configuration.value?.productLineTitle +
        ' ' +
        configuration.value?.productTitle
    )
  }
}

onMounted(async () => {
  await nextTick()

  await selectARMode()

  if (sharedConfigurationSlug.value && configuratorContainerRef.value) {
    const y =
      (configuratorContainerRef.value?.parentElement?.parentElement?.getBoundingClientRect()
        .top || 0) + window.scrollY

    scrollTo({
      top: y,
      behavior: 'smooth',
    })
  }

  // This was used to generate names of the glb files:
  const nanoid = customAlphabet('1234567890abcdef', 10)
  window.nanoid = nanoid

  if (canvasRef.value) {
    loading.value = true

    configurator3d.setMembers(canvasRef.value)
    const { addConfiguratorLights } = useEnvironment()
    addConfiguratorLights()

    const debouncedResize = useDebounceFn(() => {
      useResize().resize(canvasWidth.value, canvasHeight.value)
    }, 1)

    watch(
      canvasWidth,
      () => {
        debouncedResize()
      },
      { immediate: true }
    )
    watch(
      canvasHeight,
      () => {
        debouncedResize()
      },
      { immediate: true }
    )

    if (configuration3d.value && configuration3dValid.value) {
      try {
        await world.loadConfiguration(configuration3d.value)
      } catch (error) {
        console.error(error)
        showConfigurationErrorToast(
          t('configurator.errors.couldNotLoad3DModel')
        )
      }
    }

    // loadAR()

    loading.value = false
  }

  mounted.value = true

  if (route.query.ar && route.query.ar === 'true') {
    if (arMode.value === ARMode.NONE) {
      toast.add({
        title: t('configurator.unsupportedDevice'),
        description: t('configurator.unsupportedDeviceDescription'),
        color: 'amber',
        timeout: 20000,
      })
      return
    }

    await loadAR()
  }
})

async function loadAR() {
  try {
    const world = useWorld()
    const gltfExporter = new GLTFExporter()

    const options = {
      binary: true,
    }

    const res = await gltfExporter.parseAsync(world.chairModel, options)

    if (!(res instanceof ArrayBuffer)) return

    const blob = new Blob([res], { type: 'application/octet-stream' })

    const loader = new GLTFLoader()

    const model = await loader.loadAsync(URL.createObjectURL(blob))

    activateAR(model.scene)
  } catch (error) {
    console.error(error)
    showConfigurationErrorToast(t('configurator.errors.couldNotLoad3DModel'))
  }
}

onUnmounted(() => {
  configurator3d.destroy()
})

const arDialogOpen = ref(false)

async function showARDialog() {
  await getConfigurationLink()
  arDialogOpen.value = true
}

const accessoryCodes = computed(() => {
  return (
    Object.values(currentValues.value)
      .filter((v) => v.isAccessory && v.partNid)
      .map((v) => {
        return v.surfaceTitle ? v.surfaceTitle + ' ' + v.partTitle : v.partTitle
      }) || []
  )
})

const fullCode = computed(() => {
  const accessoryCodesTmp = accessoryCodes.value.join(' + ')

  const configurationCodes =
    configuration.value?.values
      .filter((v) => !v.isAccessory && v.partNid && v.partCode?.length)
      .map((v) => {
        return v.surfaceTitle ? v.surfaceTitle + ' ' + v.partCode : v.partCode
      })
      .join(' + ') || ''

  return accessoryCodesTmp + ' + ' + configurationCodes
})

const download3dModelName = computed(() => {
  return (
    configuration.value?.productLineTitle +
    '-' +
    configuration.value?.productTitle +
    (accessoryCodes.value?.length ? '_' + accessoryCodes.value?.join('_') : '')
  )
})

const { isFeatureEnabled } = useFeatureFlags()
</script>

<template>
  <div v-if="fatalError" class="container py-20 xl:py-40">
    <h2 class="font-pragmatica-ext mb-8 text-4xl font-bold">
      {{ $t('configurator.title') }}
    </h2>
    {{ $t('configurator.errors.unableToLoad') }}
    <span class="font-pragmatica-ext inline font-bold">
      {{ fatalError }}
    </span>
  </div>
  <UiSection v-else id="configurator" :title="$t('configurator.title')">
    <div
      v-if="
        (!configuratorIsVisible || filesIsVisible || productDetailsIsVisible) &&
        isDifferentProductSet &&
        !isClosed
      "
      class="fixed bottom-0 left-0 z-50 max-w-xl bg-blue-100 p-4 sm:p-8"
    >
      <div class="font-pragmatica-ext mb-4 text-balance text-xl font-bold">
        {{ $t('configurator.differentProduct.title') }}
      </div>
      <div class="mb-2">
        {{ $t('configurator.differentProduct.goToProductPage') }}
      </div>
      <div class="flex w-full justify-between gap-x-5 pt-2">
        <UiButton class="border-[2px] border-black" @click="isClosed = true">
          {{ $t('configurator.differentProduct.stay') }}
        </UiButton>
        <UiButtonBlack @click="navigateToConfigurationLink">
          {{ $t('configurator.differentProduct.go') }}
        </UiButtonBlack>
      </div>
    </div>
    <div
      ref="configuratorContainerRef"
      class="grid grid-cols-1 gap-1 overflow-clip rounded-sm border-8 border-neutral-200 bg-neutral-200 xl:min-h-[80svh] xl:grid-cols-12"
    >
      <div
        class="flex flex-col justify-between gap-1 xl:col-span-7"
        @click="handleConfiguratorClick"
      >
        <div class="flex h-full flex-col gap-1">
          <div
            v-if="activePanel === null"
            class="flex flex-col justify-center gap-2 bg-white p-5 2xl:p-10"
          >
            <p class="font-pragmatica-ext text-xl font-bold 2xl:text-3xl">
              {{
                configuration?.productLineTitle +
                ' ' +
                configuration?.productTitle
              }}
            </p>
            <p
              v-if="isFeatureEnabled('showFullCodeInConfigurator')"
              class="text-neutral-400"
            >
              {{ fullCode }}
            </p>
          </div>

          <button
            v-else
            class="font-pragmatica-ext top-0 flex w-full items-center bg-white p-5 text-xl font-bold hover:bg-neutral-100 2xl:p-10 2xl:text-3xl"
            @click="activePanel = null"
          >
            <Icon name="fa6-solid:arrow-left" class="mr-4 font-bold" />
            <span>
              {{ panels.get(activePanel) }}
            </span>
          </button>
          <div
            class="grid flex-grow grid-cols-[4rem_1fr] gap-1 overflow-clip 2xl:grid-cols-[5rem_1fr]"
          >
            <div
              class="col-span-2 flex flex-col bg-white"
              :class="{
                '!col-span-1 h-full': activePanel !== null,
              }"
            >
              <ProductConfiguratorMetasAccessoriesOptions
                v-model="activePanel"
                :values="values"
                :configuration="configuration"
                :multiselectable-accessories="multiselectableAccessories"
                :is3d-valid="configuration3dValid"
                :is-metas="true"
              />
              <ProductConfiguratorOptionalBasePart
                v-model="activePanel"
                :optional-base-parts="optionalBaseParts"
                @toggle-optional-base-part="
                  (partCategoryNid) => {
                    toggleOptionalBasePart(partCategoryNid)
                    loadConfiguration()
                  }
                "
              />
              <ProductConfiguratorMetasAccessoriesOptions
                v-model="activePanel"
                :values="values"
                :configuration="configuration"
                :multiselectable-accessories="multiselectableAccessories"
                :is3d-valid="configuration3dValid"
                :is-metas="false"
              />
              <ProductConfiguratorSurfacesOptions
                v-if="surfaceOptionsGroups.size > 0"
                v-model="activePanel"
                :current-values="currentValues"
                :configuration="configuration"
                :surface-category-mismatch="surfaceCategoryMismatch"
                :global-surface-changes="globalSurfaceChanges"
                :surface-types="surfaceTypes"
                :surface-maps="surfaceMaps"
                :surface-options-groups="surfaceOptionsGroups"
                :surface-options-groups-prices="surfaceOptionsGroupsPrices"
                @set-chosen-parts="setChosenParts"
                @set-global-surface-changes="setGlobalSurfaceChanges"
              />
            </div>
            <div
              v-if="activePanel !== null"
              class="col-span-1 h-full bg-neutral-200 @container/main"
            >
              <ProductConfiguratorSurfacesOptionsPanel
                :active-panel="activePanel"
                :chosen-parts="chosenParts"
                :configuration="configuration"
                :current-values="currentValues"
                :global-surface-changes="globalSurfaceChanges"
                :surface-types="surfaceTypes"
                :surface-maps="surfaceMaps"
                :surface-options-groups="surfaceOptionsGroups"
                :surface-category-mismatch="surfaceCategoryMismatch"
                :active-surface-option="activeSurfaceOption"
                @update-surfaces="updateSurfaces"
                @set-chosen-parts="setChosenParts"
                @set-global-surface-changes="setGlobalSurfaceChanges"
              />
              <ProductConfiguratorMetasOptionsPanel
                :active-panel="activePanel"
                :values="values"
                :configuration="configuration"
                @load-configuration="loadConfiguration"
              />
              <ProductConfiguratorAccessoriesOptionsPanel
                :active-panel="activePanel"
                :values="values"
                :configuration="configuration"
                :multiselectable-accessories="multiselectableAccessories"
                @apply-accessory-change="
                  async (option, concreteOption) => {
                    await checkAndApplyAccessoryChange(option, concreteOption)
                  }
                "
              />
              <ProductConfiguratorOptionalBasePartPanel
                :active-panel="activePanel"
                :optional-base-parts="optionalBaseParts"
                @toggle-optional-base-part="
                  (partCategoryNid) => {
                    toggleOptionalBasePart(partCategoryNid)
                    loadConfiguration()
                  }
                "
              />
            </div>
          </div>
        </div>
        <div class="flex h-full flex-col gap-1">
          <ProductConfigurationOrderCountAndPrice
            v-model="count"
            :price="price"
            :currency="configuration?.currency"
          />
          <div
            v-if="requestOpen"
            class="bg-white px-3 pb-3 pt-3 md:px-5 md:pb-5 lg:px-10 lg:pb-10"
          >
            <div class="flex justify-end">
              <Icon
                class="cursor-pointer"
                name="ic:baseline-close"
                size="30"
                @click="requestOpen = false"
              />
            </div>
            <ProductOrderForm
              dense
              hide-submit
              :product-title="`${configuration?.productLineTitle} ${configuration?.productTitle}`"
              :submit-trigger="requestSubmitTrigger"
              :configuration-slug="configurationSlug"
              @valid="(isValid) => (requestValid = isValid)"
              @close="requestOpen = false"
            />
          </div>
          <div
            class="flex grow flex-col justify-between gap-5 bg-white p-5 md:flex-row md:items-end"
          >
            <div
              class="flex flex-col justify-start gap-4 sm:flex-row sm:items-center md:justify-normal"
            >
              <UiButtonNeutral @click="resetConfiguration()">
                <Icon name="fa6-solid:arrows-rotate"></Icon>
                {{ $t('configurator.resetConfiguration') }}
              </UiButtonNeutral>
              <!--              TODO #25014        -->
              <UiButtonNeutral type="accent" @click="openPdfPage()">
                <Icon name="material-symbols:picture-as-pdf"></Icon>
                PDF
              </UiButtonNeutral>
              <UiButtonNeutral @click="shareModalOpen = true">
                <Icon name="fa6-solid:share"></Icon>
                {{ $t('configurator.share.title') }}
              </UiButtonNeutral>
            </div>
            <div class="flex items-center justify-end gap-4">
              <UiTooltip
                v-if="
                  [...surfaceCategoryMismatch.values()]
                    .map((group) => [...group.values()])
                    .flat()
                    .some((val) => val)
                "
                placement="bottom"
                :label="$t('configurator.categoryMismatch')"
                :offset-size="5"
                class="max-w-sm whitespace-normal break-normal rounded-none border-[1px] border-neutral-200 px-3 py-3 text-sm font-normal drop-shadow-sm"
              >
                <span
                  class="flex size-10 items-center justify-center rounded-full bg-red-500 text-white"
                >
                  <Icon
                    class="size-5 pb-0.5"
                    name="fa6-solid:triangle-exclamation"
                  />
                </span>
              </UiTooltip>
              <UiButtonBlack v-if="!requestOpen" @click="requestOpen = true">
                {{ $t('base.order.toOrder') }}
              </UiButtonBlack>
              <button
                v-else
                class="w-full bg-black px-4 py-2 text-xl font-bold text-white disabled:bg-neutral-400"
                type="submit"
                @click="requestSubmitTrigger++"
              >
                {{ $t('base.order.submit') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="relative h-full bg-white xl:col-span-5">
        <div
          class="sticky top-[10svh] h-full max-h-[50svh] min-h-[50svh] xl:max-h-[80svh]"
        >
          <div
            v-if="loading"
            class="absolute z-20 grid h-full w-full place-content-center bg-white bg-opacity-40"
          >
            <Icon
              name="line-md:loading-twotone-loop"
              class="h-16 w-16 text-black"
            />
          </div>
          <div
            v-if="mounted && configuration3d && !configuration3dValid"
            class="absolute grid h-full w-full grid-cols-1 grid-rows-1 place-content-center overflow-hidden"
          >
            <p
              v-if="!loading"
              class="font-pragmatica-ext z-10 col-start-1 row-start-1 max-w-72 place-self-center text-center text-2xl font-bold sm:max-w-96 sm:text-3xl"
            >
              {{ $t('configurator.nonValid3dModel') }}
            </p>
            <NuxtImg
              v-if="props.imagePlaceholder"
              :src="withHost(props.imagePlaceholder.url)"
              sizes="340px sm:600px"
              class="z-0 col-start-1 row-start-1 h-full w-full object-contain opacity-60 blur-sm"
              :class="{
                '!opacity-40': true,
              }"
            />
          </div>
          <canvas
            id="canvasRef"
            ref="canvasRef"
            class="!h-full !w-full"
            :class="{
              hidden: !configuration3d || !configuration3dValid,
            }"
            @wheel="stopReset()"
            @mousedown="stopReset()"
            @touchstart="stopReset()"
          ></canvas>

          <UiButtonNeutral
            v-if="
              !loading &&
              configuration3dValid &&
              (arMode === ARMode.QUICK_LOOK || arMode === ARMode.SCENE_VIEWER)
            "
            class="absolute left-5 top-5"
            @click="loadAR()"
          >
            <Icon name="fa6-solid:cube" />
            AR
          </UiButtonNeutral>

          <UiButtonNeutral
            v-if="
              !loading &&
              configuration3dValid &&
              arMode === ARMode.NONE &&
              !IS_MOBILE
            "
            class="absolute left-5 top-5"
            @click="showARDialog()"
          >
            <Icon name="fa6-solid:cube" />
            <span class="max-sm:hidden">
              {{ $t('configurator.showInYourSpace') }}
            </span>
          </UiButtonNeutral>

          <UiButtonNeutral
            v-if="!loading && configuration3dValid"
            class="absolute right-5 top-5"
            @click="reset()"
          >
            <Icon name="fa6-solid:camera-rotate" />
            {{ $t('configurator.resetView') }}
          </UiButtonNeutral>

          <UiButtonNeutral
            v-if="!loading && configuration3dValid"
            class="absolute bottom-5 left-1/2 -translate-x-1/2"
            @click="download3dModel(download3dModelName)"
          >
            <Icon name="fa6-solid:cubes"></Icon>
            {{ $t('configurator.download3dModel') }}
          </UiButtonNeutral>
        </div>
      </div>
    </div>
    <UiModal v-model="arDialogOpen" closeable>
      <div class="flex flex-col items-center gap-4">
        <QrCode :text="configurationLink + '&ar=true'" />
        <p class="font-pragmatica-ext text-center text-xl font-bold">
          {{ $t('configurator.arInstructions') }}
        </p>
      </div>
    </UiModal>
    <ProductShareConfiguration
      v-model="shareModalOpen"
      :link="configurationLink"
      :loading="configurationLinkLoading"
    />
    <ProductConfiguratorAccessoriesChangeModal
      v-if="changeOrigin"
      v-model="changeModal"
      :origin="changeOrigin"
      :accessories="changedAccessories"
      @close="changeModal = false"
    />
  </UiSection>
</template>
